import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import React, { type ReactElement } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { ProductFileTypeToLabel } from "../constants"

import FileArea from "@/components/upload/components/FileArea"
import {
  ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC,
  IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
} from "@/components/upload/constants"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import type { ProductFilesFormValues } from "@/screens/Products/components/Product/Information/schemas/ProductFilesSchema"
import { ProductFileType } from "@/screens/Products/components/Product/Information/types/productFile"

const options = Object.entries(ProductFileTypeToLabel).map(([id, value]) => (
  <MenuItem key={id} value={id}>
    {value}
  </MenuItem>
))

interface AddProductFileFormProps {
  fileNumber: number
  onMarkFileAsNeedRemoval: (fileId: string) => void
  onRemoveSingleFileForm: (id: number) => void
}

export const AddProductFileForm = ({
  fileNumber = 1,
  onMarkFileAsNeedRemoval,
  onRemoveSingleFileForm,
}: AddProductFileFormProps): ReactElement => {
  const methods = useFormContext<ProductFilesFormValues>()
  const { control, watch } = methods

  const getFileType = (index: number): ProductFileType => {
    return watch(`${index}.fileType`) || ""
  }
  const isFileDescriptionRequired = (index: number) => {
    const fileType = getFileType(index)
    return fileType === ProductFileType.Labwork || fileType === ProductFileType.Miscellaneous
  }

  return (
    <form>
      {Array.from({ length: fileNumber }).map((_, idx) => (
        // Using index as key is safe here because the list length and order are stable
        <Box key={`file-${idx}`} className='mb-10 flex flex-row items-start gap-4'>
          <Box className='grow'>
            <Controller
              name={`${idx}.fileType`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth data-testid={`${idx}.fileType`} className='mb-3'>
                  <InputLabel id={`${idx}.fileType`} className={FIELD_LABEL_CLASSES}>
                    Document Type<span className='text-red-600'>*</span>
                  </InputLabel>
                  <Select
                    {...field}
                    {...SELECT_STYLING_PROPS}
                    labelId={`${idx}.fileType`}
                    defaultValue={field.value}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span className='text-gray-500'>Select One</span>
                      }

                      return ProductFileTypeToLabel[selected as FieldGroupEnum] ?? ""
                    }}
                    error={!!error}
                  >
                    {options}
                  </Select>
                  {error?.message && (
                    <p className={FIELD_ERROR_CLASSES} data-testid='location-code-error'>
                      {error.message}
                    </p>
                  )}
                </FormControl>
              )}
            />
            {isFileDescriptionRequired(idx) && (
              <Controller
                name={`${idx}.fileDescription`}
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <Box data-testid={`${idx}.fileDescription`}>
                    <InputLabel id={`${idx}.fileDescription`} className={FIELD_LABEL_CLASSES}>
                      Document Description<span className='text-red-600'>*</span>
                    </InputLabel>
                    <TextField
                      {...field}
                      id={`${idx}.fileDescription`}
                      autoComplete='off'
                      placeholder='Document Description'
                      fullWidth
                      multiline
                      error={!!error}
                    />
                    {error?.message && (
                      <p className={FIELD_ERROR_CLASSES} data-testid='location-code-error'>
                        {error.message}
                      </p>
                    )}
                  </Box>
                )}
              />
            )}
            <FileArea
              name={`${idx}.file`}
              fieldGroups={Object.keys(ProductFileTypeToLabel).map((val) => val as unknown as FieldGroupEnum)}
              referenceTableName={ReferenceTableName.Product}
              onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval}
              showUploadAreaByDefault={true}
              allowedMimeTypes={IMAGE_MIME_TYPES_WITH_PDF_AND_DOC}
              allowedTypesLabel={ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC}
              uploadAreaTitle='Click to upload'
            />
          </Box>
          <Box className='max-w-[36px] pt-[30px]'>
            <DeleteOutlined
              fontSize='medium'
              className='cursor-pointer text-gray-900'
              onClick={() => onRemoveSingleFileForm(idx)}
            />
          </Box>
        </Box>
      ))}
    </form>
  )
}
